import { Component, ChangeDetectionStrategy, input, viewChild, effect, signal, computed } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { ChartData, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';

export interface IPieChartDataItem {
  label: string;
  value: number;
  color: string;
  id?: string | null;
}

@Component({
  selector: 'app-pie-chart',
  templateUrl: './pie-chart.component.html',
  styleUrl: './pie-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.8)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
      ]),
    ]),
  ],
})
export class PieChartComponent {
  readonly chartData = input<IPieChartDataItem[] | null>(null);
  readonly showLegend = input(true);
  readonly elevatedTooltip = input(false);

  readonly chart = viewChild<BaseChartDirective>(BaseChartDirective);

  readonly pieChartData = signal<ChartData<'pie'> | null>(null);
  readonly pieChartType = signal('pie' as const);
  readonly pieChartPlugins = signal([DataLabelsPlugin]);

  readonly pieChartOptions = computed(() => this.createChartOptions());

  constructor() {
    effect(
      () => {
        const data = this.chartData();
        this.updateChartData(data);
      },
      { allowSignalWrites: true }
    );
  }

  private updateChartData(chartData: IPieChartDataItem[] | null): void {
    if (!chartData || chartData.length === 0) {
      this.pieChartData.set(null);
      return;
    }

    const labels = chartData.map((item) => item.label);
    const data = chartData.map((item) => item.value);
    const backgroundColor = chartData.map((item) => item.color);

    this.pieChartData.set({
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderWidth: 0,
          hoverOffset: 4,
        },
      ],
    });

    setTimeout(() => this.chart()?.update(), 100);
  }

  private formatNumberWithCommas(value: number): string {
    return value.toLocaleString('en-US');
  }

  private createChartOptions(): ChartOptions<'pie'> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.showLegend(),
          position: 'bottom',
          labels: {
            boxWidth: 12,
            boxHeight: 12,
            padding: 16,
            usePointStyle: true,
            pointStyle: 'circle',
            font: {
              size: 12,
              family: 'Inter',
              weight: '400',
            },
            color: '#050320',
            generateLabels: (chart) => {
              const data = chart.data;
              if (data.labels && data.datasets.length) {
                const dataset = data.datasets[0];
                const total = dataset.data.reduce(
                  (acc: number, val: unknown) => acc + (typeof val === 'number' ? val : 0),
                  0
                );

                return data.labels.map((label, i) => {
                  const value = dataset.data[i] as number;
                  const formattedValue = this.formatNumberWithCommas(value);
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                  return {
                    text: `${label} ${formattedValue} (${percentage}%)`,
                    fillStyle: Array.isArray(dataset.backgroundColor)
                      ? dataset.backgroundColor[i]
                      : dataset.backgroundColor,
                    strokeStyle: Array.isArray(dataset.backgroundColor)
                      ? dataset.backgroundColor[i]
                      : dataset.backgroundColor,
                    lineWidth: 0,
                    hidden: false,
                    index: i,
                  };
                });
              }
              return [];
            },
          },
        },
        tooltip: this.elevatedTooltip()
          ? {
              enabled: false,
              external: (context) => {
                const { chart, tooltip } = context;
                let tooltipEl = chart.canvas.parentNode?.querySelector('.chart-tooltip') as HTMLElement;

                if (!tooltipEl) {
                  tooltipEl = document.createElement('div');
                  tooltipEl.className = 'chart-tooltip';
                  tooltipEl.style.cssText = `
                    position: absolute;
                    background: #292929;
                    color: #FFFFFF;
                    border-radius: 4px;
                    padding: 8px;
                    font-size: 12px;
                    font-family: 'Inter', sans-serif;
                    pointer-events: none;
                    z-index: 100;
                    transition: opacity 0.2s;
                  `;
                  chart.canvas.parentNode?.appendChild(tooltipEl);
                }

                if (tooltip.opacity === 0) {
                  tooltipEl.style.opacity = '0';
                  return;
                }

                if (tooltip.body) {
                  const dataIndex = tooltip.dataPoints[0].dataIndex;
                  const label = chart.data.labels?.[dataIndex] || '';
                  const value = tooltip.dataPoints[0].parsed;
                  const formattedValue = this.formatNumberWithCommas(value);
                  const total = (chart.data.datasets[0].data as number[]).reduce(
                    (acc, val) => acc + (typeof val === 'number' ? val : 0),
                    0
                  );
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                  tooltipEl.innerHTML = `${label}: ${formattedValue} (${percentage}%)`;
                }

                tooltipEl.style.opacity = '1';
                tooltipEl.style.left = tooltip.caretX + 'px';
                tooltipEl.style.top = tooltip.caretY + 'px';
                tooltipEl.style.transform = 'translate(-50%, -100%)';
              },
            }
          : {
              enabled: true,
              backgroundColor: '#292929',
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              borderColor: '#292929',
              borderWidth: 1,
              cornerRadius: 4,
              padding: 8,
              displayColors: true,
              boxWidth: 12,
              boxHeight: 12,
              titleFont: {
                size: 12,
                family: 'Inter',
              },
              bodyFont: {
                size: 12,
                family: 'Inter',
              },
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = context.parsed;
                  const formattedValue = this.formatNumberWithCommas(value);
                  const total = context.dataset.data.reduce(
                    (acc: number, val: unknown) => acc + (typeof val === 'number' ? val : 0),
                    0
                  );
                  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                  return `${label}: ${formattedValue} (${percentage}%)`;
                },
              },
            },
        datalabels: {
          display: false,
        },
      },
    };
  }
}
